import { useState } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import styled from 'styled-components';
import { Flex, FlexColumn } from '@elementary/common-ui/components';
import ConnectTicketDropdown from 'shared/components/ConnectTicketDropdown';
import IncidentTicketCreateDialog from 'incidents/components/IncidentTicketCreateDialog';
import { isIncidentReadOnly } from 'incidents/utils/incidentState';
import IncidentTicketList from 'incidents/components/IncidentTicketList';
import UnlinkIncidentTicketDialog from 'incidents/components/UnlinkIncidentTicketDialog';
import useIncidentTickets from 'incidents/hooks/useIncidentTickets';
import useIncidentTicketing from 'incidents/hooks/useIncidentTicketing';
import type { IncidentDetail, IncidentTicketRow } from 'incidents/types';

const Section = styled(FlexColumn)`
  gap: 8px;
`;

type Props = {
  incident: IncidentDetail;
};

const IncidentTickets = ({ incident }: Props) => {
  const isReadOnly = isIncidentReadOnly(incident);
  const [ticketToUnlink, setTicketToUnlink] =
    useState<IncidentTicketRow | null>(null);
  const { data: tickets = [], isLoading } = useIncidentTickets({
    incidentId: incident.id,
  });
  const {
    selectedEnvId,
    enabledIntegrations,
    ticketDialogOpen,
    selectedIntegration,
    onCreateTicketClick,
    handleDialogConfirm,
    handleDialogClose,
    isCreatingTicket,
    dialogError,
    incidentUrl,
    isResolved,
    attachTicket,
    isAttaching,
    unlinkTicket,
    isUnlinking,
  } = useIncidentTicketing({
    incidentId: incident.id,
    status: incident.status,
  });

  const hasTicketRows = tickets.length > 0;
  const canUnlinkTicket = !isReadOnly && !isResolved;
  const canManageTickets =
    !isReadOnly &&
    !isResolved &&
    (enabledIntegrations?.length ?? 0) > 0 &&
    !!selectedEnvId;
  const shouldShowCreateTicketDialog =
    ticketDialogOpen && !!selectedIntegration && !!selectedEnvId;

  if (!hasTicketRows && !canManageTickets && !isLoading) {
    return null;
  }

  return (
    <Section>
      <Flex alignItems="center" justifyContent="space-between" gap={2}>
        <Typography variant="subtitle2" fontWeight={600}>
          Tickets
        </Typography>
        {isLoading && <CircularProgress size={16} />}
      </Flex>

      <Flex gap={1} flexWrap="wrap">
        <IncidentTicketList
          tickets={tickets}
          onUnlink={canUnlinkTicket ? setTicketToUnlink : undefined}
        />
        {canManageTickets && selectedEnvId && enabledIntegrations ? (
          <ConnectTicketDropdown
            envId={selectedEnvId}
            enabledIntegrations={enabledIntegrations}
            isAttaching={isAttaching}
            isCreatingTicket={isCreatingTicket}
            onSelect={async (integration, ticket) => {
              await attachTicket({
                ticketingIntegrationId: integration.id,
                ticket,
              });
            }}
            onCreateClick={(integration) => onCreateTicketClick(integration.id)}
          />
        ) : null}
      </Flex>

      {shouldShowCreateTicketDialog && (
        <IncidentTicketCreateDialog
          open={ticketDialogOpen}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
          incidentId={incident.id}
          integration={selectedIntegration}
          envId={selectedEnvId}
          incidentUrl={incidentUrl}
          isCreating={isCreatingTicket}
          error={dialogError}
        />
      )}

      {ticketToUnlink ? (
        <UnlinkIncidentTicketDialog
          ticket={ticketToUnlink}
          isUnlinking={isUnlinking}
          onClose={() => setTicketToUnlink(null)}
          onConfirm={unlinkTicket}
        />
      ) : null}
    </Section>
  );
};

export default IncidentTickets;
